'use client'

import { SiGithub } from '@icons-pack/react-simple-icons'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Link } from '@/components/ui/link'
import { useSignInDialog } from '@/hooks/use-sign-in-dialog'
import { usePathname } from '@/i18n/routing'
import { authClient } from '@/lib/auth-client'
import { captureClientEvent } from '@/lib/posthog-client'
import { POSTHOG_EVENTS } from '@/lib/posthog-events'

import { Spinner } from './ui/spinner'

type Provider = 'github' | 'google'

function GoogleIcon() {
  return (
    <svg version='1.1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'>
      <path
        fill='#EA4335'
        d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
      />
      <path
        fill='#4285F4'
        d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'
      />
      <path
        fill='#FBBC05'
        d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'
      />
      <path
        fill='#34A853'
        d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'
      />
      <path fill='none' d='M0 0h48v48H0z' />
    </svg>
  )
}

export function SignInDialog() {
  const { open, setOpen, closeDialog: closeSignInDialog } = useSignInDialog()
  const [isPending, setIsPending] = useState(false)
  const [lastUsedProvider, setLastUsedProvider] = useState<Provider | null>(null)
  const t = useTranslations()
  const pathname = usePathname()
  const locale = useLocale()

  useEffect(() => {
    const provider = localStorage.getItem('last-used-provider') as Provider | null
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setLastUsedProvider(provider)
  }, [])

  useEffect(() => {
    if (!open) return

    captureClientEvent(POSTHOG_EVENTS.uiSignInDialogOpened, {}, { locale, pathname })
  }, [locale, open, pathname])

  async function handleSignIn(provider: Provider) {
    localStorage.setItem('last-used-provider', provider)
    captureClientEvent(POSTHOG_EVENTS.authSignInStarted, { provider }, { locale, pathname })

    await authClient.signIn.social({
      provider,
      callbackURL: `/${locale}${pathname}`,
      fetchOptions: {
        onSuccess: () => {
          setIsPending(false)
        },
        onError: () => {
          setIsPending(false)
          captureClientEvent(
            POSTHOG_EVENTS.authSignInFailed,
            {
              provider,
              error_kind: 'social_sign_in_failed',
            },
            { locale, pathname },
          )
          toast.error(t('error.sign-in-error'))
        },
        onRequest: () => {
          setIsPending(true)
        },
      },
    })
  }

  function closeDialog() {
    if (!isPending) {
      closeSignInDialog()
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='sm:max-w-120'>
        <DialogHeader>
          <DialogTitle className='text-left text-2xl'>{t('common.sign-in')}</DialogTitle>
          <DialogDescription className='text-left'>{t('dialog.sign-in.description')}</DialogDescription>
        </DialogHeader>
        <div className='my-6 flex flex-col gap-4'>
          <Button
            className='relative'
            size='lg'
            onClick={async () => handleSignIn('github')}
            disabled={isPending}
            data-testid='github-sign-in-button'
          >
            {isPending ? <Spinner /> : <SiGithub />}
            {t('dialog.sign-in.continue-with', { provider: 'GitHub' })}
            {lastUsedProvider === 'github' && <LastUsed />}
          </Button>
          <Button
            className='relative border-border'
            size='lg'
            variant='ghost'
            onClick={async () => handleSignIn('google')}
            disabled={isPending}
          >
            {isPending ? <Spinner /> : <GoogleIcon />}
            {t('dialog.sign-in.continue-with', { provider: 'Google' })}
            {lastUsedProvider === 'google' && <LastUsed />}
          </Button>
        </div>
        <div className='text-center text-xs text-muted-foreground'>
          {t.rich('dialog.sign-in.notice', {
            terms: (chunks) => (
              <Link href='/terms' className='text-foreground underline' onClick={closeDialog}>
                {chunks}
              </Link>
            ),
            privacy: (chunks) => (
              <Link href='/privacy' className='text-foreground underline' onClick={closeDialog}>
                {chunks}
              </Link>
            ),
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function LastUsed() {
  const t = useTranslations()

  return (
    <Badge variant='outline' className='absolute -top-2 -right-2 bg-background'>
      {t('dialog.sign-in.last-used')}
    </Badge>
  )
}
